'use client';

import { useState } from 'react';
import { postWaitlist } from '@/lib/api';
import { track } from '@/lib/events';

export function WaitlistModal({
  reason,
  onClose,
}: {
  reason: 'budget-exhausted' | 'session-end';
  onClose: () => void;
}) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'done' | 'error'>('idle');

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!email || status === 'submitting') return;
    setStatus('submitting');
    try {
      await postWaitlist(email);
      setStatus('done');
      track('waitlist_signup', { reason });
    } catch {
      setStatus('error');
      track('waitlist_signup_failed', { reason });
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-surface/90 backdrop-blur-md px-6">
      <div className="w-full max-w-md border border-outline-variant bg-surface-container-low p-8">
        <p className="font-mono text-[10px] tracking-widest text-primary mb-6">
          {reason === 'budget-exhausted' ? 'SYSTEM // BUDGET_EXHAUSTED' : 'SYSTEM // SESSION_COMPLETE'}
        </p>
        <h2 className="text-xl font-semibold text-on-surface tracking-tight mb-4">
          {reason === 'budget-exhausted' ? 'Out of time for today' : 'Session ended'}
        </h2>
        <p className="text-sm leading-relaxed text-on-surface-variant mb-8">
          {reason === 'budget-exhausted'
            ? "You've used your 5 minutes for today. Leave your email and we'll notify you when paid sessions open up."
            : 'Hope that was useful. Leave your email for updates on deeper sessions, the podcast, or what comes next.'}
        </p>

        {status === 'done' ? (
          <p className="font-mono text-[10px] tracking-widest text-primary border border-outline-variant px-4 py-3 mb-6">
            ● LOGGED — WE WILL REACH OUT WHEN THERE IS SOMETHING WORTH SAYING
          </p>
        ) : (
          <form onSubmit={submit} className="space-y-3 mb-6">
            <input
              type="email"
              required
              autoFocus
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-surface border border-outline-variant px-3 py-2.5 text-sm font-mono text-on-surface placeholder:text-outline focus:border-primary-bright focus:outline-none transition-colors"
            />
            <input type="text" tabIndex={-1} autoComplete="off" name="company" className="hidden" />
            <button
              type="submit"
              disabled={status === 'submitting'}
              className="w-full bg-primary-bright text-on-primary font-mono text-xs font-bold tracking-widest px-4 py-3 hover:bg-primary transition-colors disabled:opacity-50"
            >
              {status === 'submitting' ? 'TRANSMITTING...' : 'JOIN WAITLIST'}
            </button>
            {status === 'error' && (
              <p className="font-mono text-[10px] tracking-wider text-error-text">
                ERROR // SUBMISSION FAILED — TRY AGAIN
              </p>
            )}
          </form>
        )}

        <button
          onClick={onClose}
          className="font-mono text-[10px] tracking-widest text-outline hover:text-on-surface-variant transition-colors"
        >
          ← CLOSE SESSION
        </button>
      </div>
    </div>
  );
}
